import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/guest-popup.css';
import { lockScroll, unlockScroll } from '../../scrollLock';

function IconClose() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414"
      />
    </svg>
  );
}

export default function GuestWelcomePopup({ popup, onClose, onRegister }) {
  const [phase, setPhase] = useState('enter');

  const handleClose = useCallback(() => {
    setPhase('leave');
    window.setTimeout(() => {
      onClose();
    }, 220);
  }, [onClose]);

  const handleRegister = useCallback(() => {
    onClose();
    onRegister?.();
  }, [onClose, onRegister]);

  useEffect(() => {
    lockScroll();
    const openTimer = window.requestAnimationFrame(() => setPhase('open'));
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);

    const displaySeconds = Number(popup?.displaySeconds) || 0;
    let autoCloseTimer;
    if (displaySeconds > 0) {
      autoCloseTimer = window.setTimeout(handleClose, displaySeconds * 1000);
    }

    return () => {
      window.cancelAnimationFrame(openTimer);
      if (autoCloseTimer) window.clearTimeout(autoCloseTimer);
      unlockScroll();
      window.removeEventListener('keydown', onKey);
    };
  }, [handleClose, popup?.displaySeconds]);

  if (!popup) return null;

  const heroStyle = popup.imageUrl
    ? { backgroundImage: `url("${popup.imageUrl}")` }
    : undefined;

  return createPortal(
    <div
      className={`guest-welcome-popup${phase === 'open' ? ' is-open' : ''}${phase === 'leave' ? ' is-closing' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="guest-welcome-popup-title"
    >
      <div className="guest-welcome-popup__overlay" onClick={handleClose} />
      <div className="guest-welcome-popup__window" onClick={(e) => e.stopPropagation()}>
        <div className="guest-welcome-popup__inner">
          <button
            type="button"
            className="guest-welcome-popup__close"
            onClick={handleClose}
            aria-label="Close"
          >
            <IconClose />
          </button>
          {popup.imageUrl ? (
            <div className="guest-welcome-popup__hero" style={heroStyle} />
          ) : null}
          <div className="guest-welcome-popup__content">
            <h2 id="guest-welcome-popup-title" className="guest-welcome-popup__title">
              {popup.title}
            </h2>
            {popup.subtitle ? (
              <p className="guest-welcome-popup__subtitle">{popup.subtitle}</p>
            ) : null}
            <button type="button" className="guest-welcome-popup__cta" onClick={handleRegister}>
              {popup.cta}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
